import { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Paper,
  Stack,
  Text,
  Button,
  Table,
  Badge,
  Center,
  Loader,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { DollarSign, Send } from 'react-feather';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { Net10PendingPayment } from '../../../../services/api/billcom';
import { formatCents, formatDateOnly, formatDateTime } from '../../../../utils/formatters';
import { SubmitSingleNet10Modal } from './SubmitSingleNet10Modal';
import { SubmitAllNet10Modal } from './SubmitAllNet10Modal';
import { Net10SentPaymentsList } from './Net10SentPaymentsList';
import {
  getNet10SessionDetail,
  WRAPPING_BADGE_STYLES,
} from '../../../../config/compensationConfig';
import { missKindLabel } from '../../../../components/coaches/missClaimText';

const EVENT_TYPE_LABELS: Record<string, string> = {
  coach_huddle: 'Huddle',
  coach_immersion: 'Immersion',
  participant_orientation: 'Orientation',
  cohort_wrap_up: 'Wrap-Up',
  one_on_one_session: '1:1 Session',
  workshop_session: 'Workshop',
  coach_debrief_flat_rate: 'Coach Debrief Flat Rate',
};

// MED-696 AC8/AC10/AC12: per-type label for 1:1 variants. Falls back to
// EVENT_TYPE_LABELS for non-1:1 rows.
const ONE_ON_ONE_TYPE_LABELS: Record<string, string> = {
  standard_1_1: '1:1 Session - Standard',
  two_on_one: '1:1 Session - 2:1 (with Manager)',
  '360_debrief': '1:1 Session - 360 Debrief',
  hogan_debrief: '1:1 Session - Hogan Debrief',
  other_assessment_debrief: '1:1 Session - Other Assessment Debrief',
};

function resolveTypeLabel(payment: Net10PendingPayment): string {
  if (payment.event_reference_type === 'coach_debrief_flat_rate') {
    return EVENT_TYPE_LABELS.coach_debrief_flat_rate;
  }
  if (payment.one_on_one_type) {
    return ONE_ON_ONE_TYPE_LABELS[payment.one_on_one_type] ?? '1:1 Session';
  }
  return EVENT_TYPE_LABELS[payment.event_type] ?? payment.event_name;
}

interface PaymentsDueTableProps {
  cohortId: string;
  /** Each payment counts once: pending excludes overdue rows. */
  onCountChange?: (pending: number, overdue: number, totalCents: number) => void;
  /** MED-1383: the shared payment refresh key; bumps refetch the Sent to Bill.com list. */
  refreshKey?: number;
  /** MED-1383: called after a submission so every payment view refetches. */
  onPaymentsChanged?: () => void;
}

type SortField = 'due_date' | 'coach_name' | 'event_type';
type SortDir = 'asc' | 'desc';

export function PaymentsDueTable({
  cohortId,
  onCountChange,
  refreshKey,
  onPaymentsChanged,
}: PaymentsDueTableProps) {
  const [payments, setPayments] = useState<Net10PendingPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('due_date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selectedPayment, setSelectedPayment] = useState<Net10PendingPayment | null>(null);
  const [singleModalOpen, setSingleModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await cohortBillcomApi.getNet10PendingPayments(cohortId);
      setPayments(data);
      const overdueCount = data.filter(p => p.is_overdue).length;
      // MED-1360: amount_cents can arrive as a string; Number() keeps this a sum, not text.
      const totalCents = data.reduce((sum, p) => sum + Number(p.amount_cents), 0);
      onCountChange?.(data.length - overdueCount, overdueCount, totalCents);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [cohortId, onCountChange]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedPayments = [...payments].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'due_date':
        return (new Date(a.due_date).getTime() - new Date(b.due_date).getTime()) * dir;
      case 'coach_name':
        return a.coach_name.localeCompare(b.coach_name) * dir;
      case 'event_type':
        // MED-696 AC11: group by the user-visible type label so 1:1 variants
        // (360 Debrief, Hogan, etc.) and Coach Debrief Flat Rate cluster apart.
        return resolveTypeLabel(a).localeCompare(resolveTypeLabel(b)) * dir;
      default:
        return 0;
    }
  });

  const handleSubmitSingle = (payment: Net10PendingPayment) => {
    setSelectedPayment(payment);
    setSingleModalOpen(true);
  };

  const handleSuccess = () => {
    fetchPayments();
    onPaymentsChanged?.();
  };

  const hasPending = payments.length > 0;
  // MED-1383: Submit All skips a coach with no Bill.com vendor, so with only those
  // rows left the button has nothing to send.
  const hasSubmittable = payments.some(p => p.billcom_vendor_id);
  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <>
      <Paper
        p="lg"
        radius="md"
        style={{ border: '1px solid #E8E6E1', background: 'white' }}
      >
        <Group justify="space-between" align="flex-start" mb="md">
          <div>
            <Text fw={700} size="lg">
              Payments Due (Net 10)
            </Text>
            <Text size="sm" c="dimmed">
              Attendance-dependent payments awaiting submission to Bill.com
            </Text>
          </div>
          <Tooltip
            label="Link these coaches to a Bill.com vendor before you submit"
            disabled={!hasPending || hasSubmittable}
          >
            {/* A disabled button fires no mouse events, so the span carries the tooltip. */}
            <span>
              <Button
                size="sm"
                color="medleyGreen.9"
                disabled={!hasSubmittable}
                onClick={() => setBulkModalOpen(true)}
              >
                Submit All Pending
              </Button>
            </span>
          </Tooltip>
        </Group>

        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : hasPending ? (
          // MED-1383: on a narrow screen the table scrolls inside its card, not the page.
          <Table.ScrollContainer minWidth={720}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('coach_name')}
                  >
                    Coach{sortIndicator('coach_name')}
                  </Table.Th>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('event_type')}
                  >
                    Event{sortIndicator('event_type')}
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Amount</Table.Th>
                  <Table.Th
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSort('due_date')}
                  >
                    Due Date{sortIndicator('due_date')}
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Status</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedPayments.map((p) => (
                  <Table.Tr key={`${p.event_reference_type}-${p.event_reference_id}-${p.coach_user_id}`}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={500}>
                          {p.coach_name}
                        </Text>
                        {/* MED-918 AC12 (Option A): "REMOVED" indicator for
                            coaches who attended the event but have since been
                            removed from the cohort. They earned the payment.
                            The badge signals why the row is still here. */}
                        {p.coach_is_removed_from_cohort && (
                          <Tooltip
                            label="This coach was removed from the cohort but earned this payment for prior attendance."
                            multiline
                            w={260}
                          >
                            <Badge size="sm" color="warningAmber" variant="light">
                              REMOVED
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {resolveTypeLabel(p)}
                      </Text>
                      {/* MED-1346: a paid no-show or late change, not a delivered session. */}
                      {p.missKind && (
                        <Badge
                          size="xs"
                          color="warningAmber"
                          variant="light"
                          styles={WRAPPING_BADGE_STYLES}
                        >
                          {missKindLabel(p.missKind)}
                        </Badge>
                      )}
                      {/* MED-1346: the missed time and the invoice number tell two misses on one 1:1 apart. */}
                      {p.missOriginalStart && (
                        <Text size="xs" c="dimmed">
                          Missed {formatDateTime(p.missOriginalStart)}
                        </Text>
                      )}
                      {p.missKind && p.invoiceNumberPreview && (
                        <Text size="xs" c="dimmed">
                          Invoice {p.invoiceNumberPreview}
                        </Text>
                      )}
                      {p.event_reference_type !== 'coach_debrief_flat_rate' && (
                        <Text size="xs" c="dimmed">
                          {p.event_name}
                        </Text>
                      )}
                      {/* MED-1383: the session date and participant tell two 1:1 rows apart. */}
                      {getNet10SessionDetail(p) && (
                        <Text size="xs" c="dimmed">
                          {getNet10SessionDetail(p)}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="sm" fw={600}>
                        {formatCents(p.amount_cents)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text
                        size="sm"
                        c={p.is_overdue ? 'dangerRed' : undefined}
                        fw={p.is_overdue ? 600 : undefined}
                      >
                        {formatDateOnly(p.due_date)}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      {p.is_overdue ? (
                        <Badge
                          size="sm"
                          color="dangerRed"
                          variant="light"
                          styles={WRAPPING_BADGE_STYLES}
                        >
                          OVERDUE
                        </Badge>
                      ) : (
                        <Badge
                          size="sm"
                          color="warningAmber"
                          variant="light"
                          styles={WRAPPING_BADGE_STYLES}
                        >
                          PENDING
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      {/* MED-1383: a disabled icon fires no mouse events, so the span
                          carries the tooltip, and the tooltip gives the reason. */}
                      <Tooltip
                        label={
                          p.billcom_vendor_id
                            ? 'Submit to Bill.com'
                            : 'Link this coach to a Bill.com vendor before you submit'
                        }
                      >
                        <span style={{ display: 'inline-flex' }}>
                          <ActionIcon
                            variant="light"
                            color="medleyGreen"
                            size="sm"
                            onClick={() => handleSubmitSingle(p)}
                            disabled={!p.billcom_vendor_id}
                            aria-label="Submit to Bill.com"
                          >
                            <Send size={14} />
                          </ActionIcon>
                        </span>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <Stack align="center" py="xl" gap="xs">
            <DollarSign size={32} color="#C4C1BC" />
            <Text size="sm" c="dimmed" ta="center">
              No attendance-dependent payments pending
            </Text>
            <Text size="xs" c="dimmed" ta="center" maw={400}>
              Payments will appear here as events are delivered and submitted for
              payment processing.
            </Text>
          </Stack>
        )}

        <Net10SentPaymentsList cohortId={cohortId} refreshKey={refreshKey} />
      </Paper>

      <SubmitSingleNet10Modal
        opened={singleModalOpen}
        onClose={() => setSingleModalOpen(false)}
        cohortId={cohortId}
        payment={selectedPayment}
        onSuccess={handleSuccess}
      />

      <SubmitAllNet10Modal
        opened={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        cohortId={cohortId}
        payments={payments}
        onSuccess={handleSuccess}
      />
    </>
  );
}
